import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams, useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { pdf } from '@react-pdf/renderer'

import { MainTitle } from '@generators/htmlComponents/MainTitle'
import { Line } from '@generators/htmlComponents/Line'
import Text from '@generators/htmlComponents/Text'
import ConsentText from '@generators/htmlComponents/ConsentText'
import InformedConsent from '@generators/informedConsent'
import { Signature } from '@generators/htmlComponents/Signature'
import { Footer } from '@generators/htmlComponents/Footer'
import Loading from '@components/loading'
import DownloadPdfButton from '@components/inputs/button/downloadPdfButton'
import ArrowBack from '@components/arrowBack'
import LoadingModal from '@components/loading/loadingModal'

import colors from '@settings/styleSettings'
import {
  Page, Document, BoldText, Section, ArrowArea
} from './styles'

import { cpfMask } from '@util/stringUtil'
import { getOnlyDateFromFormattedDateStr } from '@util/timeUtil'
import { getReportToInsurance as getReportToInsuranceAction } from '@actions/beneficiary'

const ConsentForm = ({ reportData, getReportToInsurance, authData }) => {
  const { beneficiaryId } = useParams()
  const [loadingModal, setLoadingModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const {
    acceptedTerms, name, cpf, assign
  } = reportData || {}

  const formattedDate = getOnlyDateFromFormattedDateStr(acceptedTerms)
  const formattedCpf = cpfMask(cpf)

  const { insurance } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    setLoadingModal(true)
    getReportToInsurance({ id: beneficiaryId }, () => {
      setLoading(false)
      setLoadingModal(false)
    })
  }, [beneficiaryId])

  const goBack = () => {
    navigate(-1)
  }

  const getReportCallback = async (report) => {
    const termsOfConsentBlob = await pdf(
      <InformedConsent beneficiary={report} insurance={insurance} />
    )
    const termsOfConsentPdf = termsOfConsentBlob && (await termsOfConsentBlob.toBlob())
    const pdfWindow = window.open()
    pdfWindow.location.href = URL.createObjectURL(termsOfConsentPdf)
  }

  const onExportToPdf = () => {
    getReportCallback(reportData)
  }

  return (
    <>
      <Document>
        <ArrowArea>
          <ArrowBack onClick={goBack} />
        </ArrowArea>
        <Page>
          <MainTitle title="Termo de consentimento" />
          <Line lineColor={colors.softGray} />
          <Text style={{ color: colors.strongGray, fontSize: 14 }}>
            Este documento visa registrar a manifestação livre, informada e
            inequívoca pela qual eu,
            <BoldText style={{ color: colors.strongGray }}>
              {' '}
              {name}
            </BoldText>
            ,
            CPF n°
            <BoldText style={{ color: colors.strongGray }}>
              {' '}
              {formattedCpf}
            </BoldText>
            , na qualidade de beneficiário titular de dados, adiante denominado
            TITULAR, concordo com o tratamento dos meus dados pessoais para
            finalidade específica de prestação de serviços de assistência
            médico-hospitalar conforme previsto na lei 9656/98, havida com a
            {' '}
            {authData.insurer.name.toUpperCase()}
            , aqui na qualidade e adiante denominada Controladora,
            em conformidade com a Lei n. 13.709/2018 – Lei Geral de Proteção de
            Dados Pessoais (LGPD).
          </Text>
          <ConsentText text="Ao manifestar minha aceitação para com o presente termo, dou meu consentimento e concordo que a Controladora tome decisões referentes ao tratamento de meus dados pessoais necessários ao usufruto dos serviços da Controladora, bem como realize o tratamento de tais dados, envolvendo operações como as que se referem a coleta, produção, recepção, classificação, utilização, acesso, reprodução, transmissão, distribuição, processamento, arquivamento, armazenamento, eliminação, avaliação ou controle da informação, modificação, comunicação, transferência, difusão ou extração." />
          <ConsentText
            title="1. Dados Pessoais: "
            text="A Controladora fica autorizada a tomar decisões referentes ao tratamento e a realizar o tratamento dos dados pessoais do Titular, imprescindíveis para manutenção da relação contratual."
          />
          <ConsentText
            title="2. Finalidades do Tratamento dos Dados: "
            text="O tratamento dos dados pessoais tem as seguintes finalidades: Permitir a identificação e o contato entre a Controladora e o Titular para fins de relacionamento comercial; Possibilitar que a Controladora elabore proposta comerciais para o Titular; Possibilitar que a Controladora forneça ao Titular os serviços dispostos no contrato de Plano de Saúde; Permitir que a Controladora alimente seu banco interno de cadastro de cliente, para fins de conhecimento do vínculo da relação comercial e a devida prestação do serviço contratado; Possibilitar que a Controladora utilize tais dados em Pesquisas de Mercado; Possibilitar que a Controladora utilize tais dados na inscrição, divulgação, premiação dos interessados participantes de campanhas ou eventos por ela promovidos; Possibilitar que a Controladora utilize tais dados na elaboração de relatórios e emissão de produtos e serviços; Possibilitar que a Controladora utilize tais dados para a emissão de Notas Fiscais e documentos financeiros correlatos."
          />
          <ConsentText
            title="3. Compartilhamento de Dados: "
            text="A Controladora fica autorizada a compartilhar os dados pessoais do Titular com outros agentes de tratamento de dados, caso seja necessário para as finalidades listadas neste termo e no contrato de Plano de Saúde, observados os princípios e as garantias estabelecidas pela Lei n.13.709/2018."
          />
          <ConsentText
            title="4. Segurança dos Dados: "
            text="A Controladora adotará todas as medidas de segurança, técnicas e administrativas aptas a proteger os dados pessoais de acessos não autorizados e de situações acidentais ou ilícitas de destruição, perda, alteração, comunicação ou qualquer forma de tratamento inadequado ou ilícito."
          />
          <ConsentText
            title="5. Término do Tratamento e Eliminação dos Dados: "
            text="A Controladora poderá manter e tratar os dados pessoais do Titular durante todo o período em que forem pertinentes ao alcance das finalidades listadas neste termo e dispostas em diplomas legais, para o interesse comum das partes pelo prazo legal."
          />
          <ConsentText
            title="6. Direitos do Titular: "
            text="O Titular tem direito a obter da Controladora, em relação aos dados por ele tratados, a qualquer momento e mediante requisição todos os direitos previstos no Art. 18 da Lei n.13.709/2018."
          />
          <ConsentText text="Este consentimento poderá ser revogado pelo Titular, a qualquer momento, mediante solicitação formal ao Controlador, ficando ciente de que poderá ser inviável à Controladora continuar o fornecimento de produtos ou serviços a partir da eliminação dos dados pessoais." />

          <Section>
            <Text style={{ color: colors.strongGray, fontSize: 14 }}>
              Assinado em
              {' '}
              {formattedDate}
            </Text>
          </Section>
          <Section>
            <Signature image={assign} beneficiaryName={name} />
            <Line lineColor={colors.softGray} />
          </Section>
          <Section>
            {authData.insurer.id === 1 ? (
              <Footer />
            ) : null}
          </Section>
        </Page>
        {loading ? (
          <Loading />
        ) : (
          <DownloadPdfButton onClick={() => onExportToPdf()} />
        )}
      </Document>
      <LoadingModal isOpen={loadingModal} />
    </>
  )
}

ConsentForm.propTypes = {
  reportData: PropTypes.shape({
    name: PropTypes.string,
    cpf: PropTypes.string,
    acceptedTerms: PropTypes.string,
    assign: PropTypes.string,
  }),
  authData: PropTypes.shape({
    insurer: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }),
  getReportToInsurance: PropTypes.func,
}

ConsentForm.defaultProps = {
  reportData: null,
  getReportToInsurance: () => {},
  authData: {},
}
const mapStateToProps = ({ beneficiary, auth }) => ({
  reportData: beneficiary.reportData,
  authData: auth.data,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getReportToInsurance: getReportToInsuranceAction,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(ConsentForm)
